import type { Pagination, Paginator } from "@/types/pagination";
import { computed, reactive, unref, type Ref, type MaybeRef, type ComputedRef } from "vue";
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_ITEMS_PER_PAGE_OPTIONS } from "@/types/pagination";

type PaginatorState = {
  pagination: Pagination;
  page: number;
}

export type PaginatorOptions = {
  pagination?: Pagination;
  onChange?: () => (void | Promise<void>)
}

export type UsePaginator = {
  pagination: Ref<Pagination>;
  page: Ref<number>;
  itemsPerPage: Ref<number>;
  setPage: (page: number) =>  void;
  setLimit: (limit: number) => void;
  goBack: () => void;
  goNext: () => void;
}

export const usePaginator = (options: PaginatorOptions = {}): UsePaginator => {
	const state = reactive<PaginatorState>({
		page: options.pagination && options.pagination.limit > 0
		? Math.floor(options.pagination.offset / options.pagination.limit)
		: 0,
		pagination: { ...(options?.pagination || { offset: 0, limit: DEFAULT_ITEMS_PER_PAGE }) }
	});

  const updateOffset = () => {
    state.pagination.offset = state.page * state.pagination.limit;
  }

  const setPage = (page: number) => {
    state.page = page;
    updateOffset();

    if (options.onChange) {
      options.onChange();
    }
  }

  const setLimit = (limit: number) => {
	if (limit <= 0) return;

	state.pagination.limit = limit;
	state.page = 0;
	updateOffset();

	if (options.onChange) {
		options.onChange();
	}
  }

  const goBack = () => {
    setPage(Math.max(0, state.page-1));
  }

  const goNext = () => {
    setPage(state.page + 1);
  }

  const pagination = computed(() => ({ ...state.pagination, limit: state.pagination.limit, offset: state.pagination.offset }));

  const page = computed(() => state.page);

  const itemsPerPage = computed(() => state.pagination.limit);

  return { pagination, page, itemsPerPage, setPage, setLimit, goBack, goNext }
}

export type CreatePaginatorViewOptions = {
  paginator: UsePaginator
  totalCount: MaybeRef<number>
  itemsPerPageOverride?: MaybeRef<number>
  itemsPerPageOptions?: number[]
}

export const createPaginatorView = (
  options: CreatePaginatorViewOptions
): ComputedRef<Paginator> => {
  return computed((): Paginator => {
    const total = unref(options.totalCount) || 0
    const itemsPerPage = options.itemsPerPageOverride
      ? unref(options.itemsPerPageOverride)
      : options.paginator.itemsPerPage.value

    return {
      pageNumber: options.paginator.page.value,
      itemsPerPage,
      pageCount: total > 0 ? Math.floor(total / itemsPerPage) + Number(total % itemsPerPage > 0) : 0,
      totalItemCount: total,
      onSelectPage: options.paginator.setPage,
      onGoNext: options.paginator.goNext,
      onGoBack: options.paginator.goBack,
      onItemsPerPageChange: options.paginator.setLimit,
      itemsPerPageOptions: options.itemsPerPageOptions || [...DEFAULT_ITEMS_PER_PAGE_OPTIONS],
    }
  })
}
